import { Button, Checkbox, Select, Textarea, TextField } from "@ths/ui";
import { useEffect, useRef, useState } from "react";
import { reportBasecampError } from "sentry";

import {
  useConfirmWorkOrderRequestAttachmentMutation,
  useCreateWorkOrderRequestMutation,
  useGenerateWorkOrderRequestAttachmentUploadUrlMutation,
  useGetIssueReportBasecampsQuery,
  WorkOrderPriority,
} from "generated/graphql";

import { trackBasecampEvent } from "../../analytics";
import { presentBasecampRequestError } from "../../api/selfCheckinError";

import PhotoUploader, { SelectedPhoto } from "./PhotoUploader";

interface Props {
  room?: { basecampRoomId: string; buildingName: string; roomNumber: string };
  basecamp?: { id: string; name: string };
  prefilledContact?: { phone: string; email: string };
  sessionToken?: string;
  assignmentId?: string;
  onSubmitted: () => void;
}

export const formatIssueLocation = ({
  buildingName,
  roomNumber,
}: NonNullable<Props["room"]>) =>
  buildingName ? `${buildingName} · Room ${roomNumber}` : `Room ${roomNumber}`;

export default function DetailsStep({
  room,
  basecamp,
  prefilledContact,
  sessionToken,
  assignmentId,
  onSubmitted,
}: Props) {
  const hasStay = Boolean(sessionToken && assignmentId);
  const [basecampId, setBasecampId] = useState(basecamp?.id ?? "");
  const [location, setLocation] = useState(() =>
    room ? formatIssueLocation(room) : "",
  );
  const [description, setDescription] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [phone, setPhone] = useState(prefilledContact?.phone ?? "");
  const [email, setEmail] = useState(prefilledContact?.email ?? "");
  const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
  const [processingPhotos, setProcessingPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdReport, setCreatedReport] = useState<{
    id: string;
    uploadToken: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);
  const submittingRef = useRef(false);
  // Retain progress after an upload failure so retry never creates a second report.
  const photoProgress = useRef(
    new Map<
      string,
      {
        url: string;
        storagePath: string;
        uploaded: boolean;
        confirmed: boolean;
      }
    >(),
  );
  const {
    data,
    loading,
    error: directoryError,
    refetch,
  } = useGetIssueReportBasecampsQuery({ skip: hasStay });
  const basecamps = data?.issueReportBasecamps ?? [];
  const [createReq] = useCreateWorkOrderRequestMutation();
  const [getUrl] = useGenerateWorkOrderRequestAttachmentUploadUrlMutation();
  const [confirmAttachment] = useConfirmWorkOrderRequestAttachmentMutation();

  useEffect(() => {
    trackBasecampEvent("basecamp_request_started", {
      flow: "request",
      hasPrefilledContact: Boolean(
        prefilledContact?.phone || prefilledContact?.email,
      ),
      surface: "web",
    });
  }, [prefilledContact?.email, prefilledContact?.phone]);

  const canSubmit =
    (hasStay || basecamps.some((camp) => camp.id === basecampId)) &&
    location.trim().length > 0 &&
    description.trim().length > 0 &&
    /[0-9]/.test(phone) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    !processingPhotos &&
    !submitting;

  const submit = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    let failureStage: "photo" | "submit" = "submit";
    try {
      let report = createdReport;
      if (!report) {
        const result = await createReq({
          variables: {
            input: {
              location: location.trim(),
              description: description.trim(),
              priority: isUrgent
                ? WorkOrderPriority.Urgent
                : WorkOrderPriority.Medium,
              contactPhone: phone.trim(),
              contactEmail: email.trim(),
              ...(hasStay ? { sessionToken, assignmentId } : { basecampId }),
            },
          },
        });
        if (!activeRef.current) return;
        const created = result.data?.createWorkOrderRequest;
        if (!created?.id || !created.uploadToken)
          throw new Error("Missing report confirmation");
        report = { id: created.id, uploadToken: created.uploadToken };
        setCreatedReport(report);
      }
      for (const photo of photos) {
        if (!activeRef.current) return;
        failureStage = "photo";
        let progress = photoProgress.current.get(photo.id);
        if (progress?.confirmed) continue;
        if (!progress) {
          const result = await getUrl({
            variables: {
              input: {
                uploadToken: report.uploadToken,
                requestId: report.id,
                filename: photo.file.name,
                contentType: photo.file.type,
              },
            },
          });
          if (!activeRef.current) return;
          const upload =
            result.data?.generateWorkOrderRequestAttachmentUploadUrl;
          if (!upload) throw new Error("Missing upload URL");
          progress = { ...upload, uploaded: false, confirmed: false };
          photoProgress.current.set(photo.id, progress);
        }
        if (!progress.uploaded) {
          const response = await fetch(progress.url, {
            method: "PUT",
            headers: { "Content-Type": photo.file.type },
            body: photo.file,
          });
          if (!activeRef.current) return;
          // A lost success response can leave the immutable slot occupied. The
          // confirmation endpoint verifies the stored object before accepting it.
          if (!response.ok && response.status !== 409) {
            const uploadError = new Error(
              "Photo upload failed. Please try again.",
            );
            reportBasecampError(uploadError, {
              feature: "work-order-request",
              action: "upload-photo",
              requestId: report.id,
              status: response.status,
            });
            throw uploadError;
          }
          progress.uploaded = true;
        }
        await confirmAttachment({
          variables: {
            input: {
              uploadToken: report.uploadToken,
              requestId: report.id,
              storagePath: progress.storagePath,
              fileName: photo.file.name,
            },
          },
        });
        if (!activeRef.current) return;
        progress.confirmed = true;
      }
      if (!activeRef.current) return;
      trackBasecampEvent("basecamp_request_submitted", {
        flow: "request",
        isUrgent,
        photoCount: photos.length,
        surface: "web",
      });
      onSubmitted();
    } catch (cause) {
      if (!activeRef.current) return;
      trackBasecampEvent("basecamp_request_failed", {
        errorCategory: "request_failed",
        flow: "request",
        isUrgent,
        photoCount: photos.length,
        surface: "web",
      });
      setError(presentBasecampRequestError(cause, failureStage).message);
    } finally {
      submittingRef.current = false;
      if (activeRef.current) setSubmitting(false);
    }
  };

  return (
    <form
      className="w-full max-w-md space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div>
        <h1 className="text-2xl font-semibold text-text">Report an issue</h1>
        <p className="mt-2 text-body text-text-secondary">
          Tell us where the issue is and how we can reach you.
        </p>
      </div>
      <fieldset
        disabled={submitting || Boolean(createdReport)}
        className="space-y-5"
      >
        {hasStay ? (
          <p className="text-body font-medium text-text">
            {basecamp?.name ?? "Your Basecamp"}
          </p>
        ) : (
          <div className="space-y-2">
            <Select
              label="Basecamp"
              value={basecampId}
              onChange={setBasecampId}
              options={basecamps.map((camp) => ({
                value: camp.id,
                label: camp.name,
              }))}
              placeholder={loading ? "Loading Basecamps…" : "Choose a Basecamp"}
              disabled={loading || submitting || Boolean(createdReport)}
            />
            {directoryError ? (
              <div role="alert" className="text-body text-error-text">
                Could not load Basecamps.{" "}
                <Button
                  type="button"
                  variant="link"
                  onClick={() => void refetch()}
                >
                  Try again
                </Button>
              </div>
            ) : !loading && basecamps.length === 0 ? (
              <p role="status" className="text-body text-text-secondary">
                No Basecamps are available. Please contact a staff member.
              </p>
            ) : null}
          </div>
        )}
        <TextField
          id="bc-location"
          label="Location"
          value={location}
          onChange={(event) => setLocation(event.target.value)}
          placeholder="Room, building, or shared area"
          maxLength={200}
          required
        />
        <Textarea
          id="bc-description"
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={4}
          placeholder="What's wrong, broken, or missing?"
          maxLength={5000}
          required
        />
        <Checkbox
          label="Mark as urgent"
          checked={isUrgent}
          onCheckedChange={setIsUrgent}
          disabled={submitting || Boolean(createdReport)}
        />
        <PhotoUploader
          photos={photos}
          onChange={setPhotos}
          disabled={submitting || Boolean(createdReport)}
          onProcessingChange={setProcessingPhotos}
        />
        <TextField
          id="bc-phone"
          label="Phone"
          type="tel"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder="(555) 555-5555"
          autoComplete="tel"
          maxLength={50}
          required
        />
        <TextField
          id="bc-email"
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          maxLength={254}
          required
        />
      </fieldset>
      {error && (
        <div role="alert" className="text-body text-error-text">
          {createdReport && (
            <p className="mb-1">
              Your report was received. Some photos could not be attached.
            </p>
          )}
          {error}
        </div>
      )}
      <Button
        type="submit"
        fullWidth
        size="lg"
        disabled={!canSubmit}
        loading={submitting}
      >
        {submitting ? "Submitting…" : createdReport ? "Retry photos" : "Submit"}
      </Button>
      {createdReport && error && (
        <Button
          type="button"
          variant="link"
          fullWidth
          disabled={submitting}
          onClick={onSubmitted}
        >
          Finish without remaining photos
        </Button>
      )}
    </form>
  );
}
